import { useState } from 'react';
import tawanaiLogo from './assets/Tawanai_logo.png';
import bgMountains from './assets/bg-mountains.png';

const inputStyle = {
  width: '100%',
  padding: '14px 18px',
  border: '1.5px solid #e2e8f0',
  borderRadius: 10,
  background: '#f8fafc',
  fontSize: 15,
  color: '#0f172a',
  outline: 'none',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};

const inputFocusStyle = {
  ...inputStyle,
  borderColor: '#3b82f6',
  boxShadow: '0 0 0 3px rgba(59,130,246,0.12)',
  background: '#fff',
};

export default function Login({ onLogin, sessionMsg }) {
  const [mode, setMode] = useState('login');
  const API_URL = process.env.REACT_APP_API_URL;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [serverStatus, setServerStatus] = useState('idle'); // idle | checking | starting
  const [retryCount, setRetryCount] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const WAKEUP_URL = process.env.REACT_APP_WAKEUP_URL;
    if (WAKEUP_URL) {
      try {
        setServerStatus('checking');
        const wakeRes = await fetch(WAKEUP_URL);
        const { status } = await wakeRes.json();
        if (status !== 'running') {
          setServerStatus('starting');
          let ready = false;
          for (let i = 0; i < 36; i++) {
            await new Promise(r => setTimeout(r, 5000));
            try {
              const h = await fetch(`${API_URL}/api/health/`);
              if (h.ok) {
                await new Promise(r => setTimeout(r, 8000)); // buffer for gunicorn to fully start
                ready = true;
                break;
              }
            } catch {}
          }
          if (!ready) {
            setServerStatus('idle'); setRetryCount(0);
            setError('Server took too long to start. Please try again.');
            return;
          }
        }
      } catch {}
      // keep banner visible during the login/register request below
    }

    const fetchWithRetry = async (url, options, maxRetries = 15) => {
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const res = await fetch(url, options);
          if (res.ok || res.status < 500) return res; // 4xx = auth/validation, return to caller
          throw new Error(`Server error ${res.status}`); // 5xx = not ready, retry
        } catch {
          if (attempt < maxRetries) {
            setRetryCount(attempt + 1);
            await new Promise(r => setTimeout(r, 8000));
          } else {
            throw new Error('Network error');
          }
        }
      }
    };

    if (mode === 'register') {
      try {
        const res = await fetchWithRetry(`${API_URL}/api/register/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password, first_name: firstName, email, invite_code: inviteCode })
        });
        const data = await res.json();
        setServerStatus('idle'); setRetryCount(0);
        if (res.ok) {
          setMode('login');
          setError('Registered! Please log in.');
        } else {
          setError(data.error || 'Registration failed');
        }
      } catch {
        setServerStatus('idle'); setRetryCount(0);
        setError('Network error');
      }
      return;
    }

    try {
      const res = await fetchWithRetry(`${API_URL}/api/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      setServerStatus('idle'); setRetryCount(0);
      if (res.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('username', username);
        localStorage.setItem('first_name', data.first_name || '');
        onLogin();
      } else {
        setError(data.error || 'Invalid credentials');
      }
    } catch {
      setServerStatus('idle'); setRetryCount(0);
      setError('Network error');
    }
  };

  const ff = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      height: '100vh',
      margin: 0,
      padding: 0,
      display: 'flex',
      overflow: 'hidden',
      fontFamily: ff,
    }}>
      {/* LEFT — hero panel */}
      <div style={{
        flex: 1.1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: `url(${bgMountains}) center/cover no-repeat`,
        position: 'relative',
        padding: '60px 48px',
      }}>
        {/* Logo */}
        <img
          src={tawanaiLogo}
          alt="Tawanai"
          style={{
            width: 110,
            height: 'auto',
            objectFit: 'contain',
            position: 'absolute',
            top: 20,
            left: 20,
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))',
          }}
        />

        {/* Hero text */}
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{
            color: '#ffffff',
            fontWeight: 800,
            fontSize: 36,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            marginBottom: 20,
            textShadow: '0 2px 12px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.4)',
          }}>
            Algeria Technical Assistance
          </div>
          <div style={{
            color: '#1e3a5f',
            fontWeight: 500,
            fontSize: 15.5,
            lineHeight: 1.65,
            background: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            borderRadius: 14,
            padding: '20px 24px',
            border: '1px solid rgba(255,255,255,0.4)',
          }}>
            Analyze the entire Algeria region for wind energy potential. Explore wind speed, infrastructure proximity, and land-use scenarios to identify optimal wind farm locations.
          </div>
        </div>
      </div>

      {/* RIGHT — auth panel */}
      <div style={{
        width: 460,
        minWidth: 380,
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '56px 52px',
        borderLeft: '1px solid #e8edf3',
        position: 'relative',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.06)',
      }}>
        {/* Nav links */}
        <div style={{
          position: 'absolute',
          top: 24,
          right: 28,
          display: 'flex',
          gap: 20,
        }}>
          {[
            { label: 'About Us', href: 'https://twa-energy.com' },
            { label: 'Contact', href: 'https://twa-energy.com/contact' },
            { label: 'LinkedIn', href: 'https://www.linkedin.com/company/tawanai-energytech/' },
          ].map(link => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: '#64748b', fontWeight: 500, fontSize: 13, transition: 'color 0.15s' }}
              onMouseEnter={e => e.target.style.color = '#0f172a'}
              onMouseLeave={e => e.target.style.color = '#64748b'}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Heading */}
        {sessionMsg && (
          <div style={{
            marginBottom: 20,
            padding: '10px 14px',
            background: '#fefce8',
            border: '1px solid #fde68a',
            borderRadius: 8,
            color: '#92400e',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.5,
          }}>
            {sessionMsg}
          </div>
        )}

        <div style={{
          fontWeight: 800,
          fontSize: 30,
          color: '#0f172a',
          marginBottom: 8,
          letterSpacing: '-0.02em',
        }}>
          {mode === 'login' ? 'Welcome back' : 'Create account'}
        </div>
        <div style={{ color: '#64748b', fontSize: 14, marginBottom: 32, fontWeight: 400 }}>
          {mode === 'login' ? 'Sign in to your account to continue.' : 'Fill in the details below to get started.'}
        </div>

        <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mode === 'register' && (
            <>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                style={focusedField === 'firstName' ? inputFocusStyle : inputStyle}
                onFocus={() => setFocusedField('firstName')}
                onBlur={() => setFocusedField(null)}
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={focusedField === 'email' ? inputFocusStyle : inputStyle}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
              />
              <input
                type="text"
                placeholder="Invite Code"
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value)}
                required
                style={focusedField === 'inviteCode' ? inputFocusStyle : inputStyle}
                onFocus={() => setFocusedField('inviteCode')}
                onBlur={() => setFocusedField(null)}
              />
            </>
          )}

          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
            style={focusedField === 'username' ? inputFocusStyle : inputStyle}
            onFocus={() => setFocusedField('username')}
            onBlur={() => setFocusedField(null)}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={focusedField === 'password' ? inputFocusStyle : inputStyle}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
          />

          {serverStatus !== 'idle' && (
            <div style={{
              padding: '12px 14px',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
              color: '#1d4ed8',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 13 }}>
                <div style={{
                  width: 14, height: 14, flexShrink: 0,
                  border: '2px solid #bfdbfe',
                  borderTopColor: '#1d4ed8',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                {serverStatus === 'checking' ? 'Checking server…' : 'Server is waking up…'}
              </div>
              {serverStatus === 'starting' && (
                <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 5, marginLeft: 24 }}>
                  {retryCount > 0
                    ? `Still connecting… attempt ${retryCount} of 15. Please keep this tab open.`
                    : 'This takes 1–2 minutes. Please keep this tab open.'}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{
              color: error.includes('Registered') ? '#059669' : '#dc2626',
              fontSize: 13,
              fontWeight: 500,
              padding: '8px 12px',
              background: error.includes('Registered') ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${error.includes('Registered') ? '#bbf7d0' : '#fecaca'}`,
              borderRadius: 8,
            }}>
              {error}
            </div>
          )}

          {mode === 'login' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' }}>
              <input type="checkbox" id="remember" style={{ accentColor: '#1e3a8a', width: 14, height: 14 }} />
              <label htmlFor="remember" style={{ cursor: 'pointer' }}>Keep me signed in</label>
            </div>
          )}

          <button
            type="submit"
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
              color: '#fff',
              fontWeight: 700,
              border: 'none',
              borderRadius: 10,
              padding: '14px 0',
              fontSize: 15,
              fontFamily: ff,
              cursor: 'pointer',
              letterSpacing: '0.01em',
              boxShadow: '0 2px 14px rgba(37,99,235,0.35)',
              transition: 'all 0.2s',
              marginTop: 4,
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 14px rgba(37,99,235,0.35)'; e.currentTarget.style.transform = 'none'; }}
          >
            {mode === 'login' ? 'Sign in' : 'Create account'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', margin: '6px 0' }}>
            <hr style={{ flex: 1, border: 0, borderTop: '1px solid #e8edf3' }} />
            <span style={{ margin: '0 14px', color: '#94a3b8', fontWeight: 500, fontSize: 13 }}>or</span>
            <hr style={{ flex: 1, border: 0, borderTop: '1px solid #e8edf3' }} />
          </div>

          <button
            type="button"
            style={{
              width: '100%',
              background: '#f1f5f9',
              color: '#0f172a',
              fontWeight: 600,
              border: '1.5px solid #e2e8f0',
              borderRadius: 10,
              padding: '13px 0',
              fontSize: 14,
              fontFamily: ff,
              cursor: 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
            onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
          >
            {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
